/**
 * Accepting an invitation.
 *
 * The person arrives from the link in their email, which has already given them a GoTrue session. What
 * they do not yet have is an ERP account bound to that identity — and, on a first visit, a password.
 *
 * The order matters: password first, then acceptance. Acceptance is what materialises their roles, and a
 * session with roles but no password is a session that cannot be re-entered.
 *
 * `api.accept_invitation()` takes no arguments. The address it acts on is the verified email inside the
 * caller's own token, so there is no parameter through which somebody could point it at another person's
 * invitation.
 */
import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useSession } from '../lib/session';
import { passwordProblem, setPassword } from '../lib/auth';
import { rpc } from '../lib/supabase';
import { Button } from '../ui/primitives';
import { Field, Input } from '../ui/forms';
import { AuthError, AuthFrame } from './AuthFrame';

interface Accepted {
  status: string;
  user_account_id: string;
  roles_granted: string[];
}

export function AcceptInvitationScreen() {
  const { hasAuthSession, authEmail, me, isLoading, refresh, signOut } = useSession();
  const [password, setPasswordValue] = useState('');
  const [confirm, setConfirm] = useState('');
  const [needsPassword, setNeedsPassword] = useState(true);

  const accept = useMutation({
    mutationFn: async () => {
      if (needsPassword) {
        await setPassword(password);
      }
      return rpc<Accepted>('accept_invitation');
    },
    onSuccess: () => refresh(),
  });

  if (hasAuthSession === false) {
    return <Navigate to="/sign-in" replace />;
  }
  if (!isLoading && me?.authenticated) {
    // Already an account: nothing to accept. The shell will decide what they may see.
    return <Navigate to="/" replace />;
  }

  const problem = passwordProblem(password);
  const mismatch = confirm.length > 0 && confirm !== password;

  if (accept.isSuccess) {
    return (
      <AuthFrame title="You are in" subtitle={`Welcome. ${accept.data.roles_granted.length > 0 ? `Your roles: ${accept.data.roles_granted.join(', ')}.` : 'An administrator will assign your roles.'}`}>
        <Button variant="primary" onClick={() => window.location.assign('/')}>
          Open the ERP
        </Button>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame
      title="Accept your invitation"
      subtitle={authEmail ? `You are signed in as ${authEmail}.` : 'Finish setting up your account.'}
      aside="If you were not expecting an invitation, sign out and tell whoever sent it. Nothing is created until you continue."
    >
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          accept.mutate();
        }}
        className="space-y-4"
      >
        {needsPassword ? (
          <>
            <Field label="Choose a password" required error={password.length > 0 ? problem : null} hint="At least 12 characters, with a letter and a number.">
              <Input type="password" autoComplete="new-password" value={password} onChange={(e) => setPasswordValue(e.target.value)} height="form" autoFocus />
            </Field>
            <Field label="Confirm password" required error={mismatch ? 'The two passwords differ.' : null}>
              <Input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} height="form" />
            </Field>
            <button type="button" onClick={() => setNeedsPassword(false)} className="text-small text-ink-muted hover:text-ink">
              I already have a password
            </button>
          </>
        ) : (
          <p className="text-body text-ink-muted">
            Your existing password stays as it is. Continue to bind this sign-in to your invitation.
          </p>
        )}

        <AuthError error={accept.error} />

        <div className="flex items-center justify-between">
          <Button type="button" variant="ghost" onClick={() => void signOut()} disabled={accept.isPending}>
            Sign out
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={accept.isPending || (needsPassword && (Boolean(problem) || mismatch || confirm.length === 0))}
          >
            {accept.isPending ? 'Setting up…' : 'Accept and continue'}
          </Button>
        </div>
      </form>
    </AuthFrame>
  );
}
